import { z } from 'zod';

import { SettingsResultSchema } from '@e-origin/contracts';
import { EnvironmentCountries } from '@e-origin/shared';

import {
  DATA_TABLE_PAGE_SIZE,
  DATA_TABLE_PAGE_SIZE_EXTRA_LARGE,
  DATA_TABLE_PAGE_SIZE_LARGE,
  DATA_TABLE_PAGE_SIZE_MEDIUM,
  DATA_TABLE_PAGE_SIZE_SMALL,
  MILLISECONDS_IN_SECOND,
  MINUTES_IN_HOUR,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from '@constants';
import { apiClient } from '@utils';

type SettingsApiResponse = z.infer<typeof SettingsResultSchema>;

interface Settings extends SettingsApiResponse {
  apiUrl: string;
  dataTablePageSizes: number[];
  jwtExpirationTime: number;
  isProd: boolean;
  isForwarder: boolean;
  country: EnvironmentCountries;
  enableNewFinalizeBatch: boolean;
  setIsForwarder: (value: boolean) => void;
  getIsForwarder: () => boolean;
}

let isForwarder = false;

const isProd = !/^(auto|e2e|test)\..*\.eu$|^(localhost|0\.0\.0\.0)$/.test(window.location.hostname);

export const defaultSettings: Partial<Settings> = {
  apiUrl: `${['localhost', '0.0.0.0', '127.0.0.1'].includes(window.location.hostname) ? ((import.meta as any).env.VITE_API_URL ?? '') : ''}/api/v1`,
  dataTablePageSizes: [DATA_TABLE_PAGE_SIZE_SMALL, DATA_TABLE_PAGE_SIZE_MEDIUM, DATA_TABLE_PAGE_SIZE_LARGE, DATA_TABLE_PAGE_SIZE_EXTRA_LARGE],
  jwtExpirationTime: DATA_TABLE_PAGE_SIZE * SECONDS_IN_HOUR,
  isProd: isProd,
  enableNewFinalizeBatch: !isProd,
  walletEnabled: false,
  isForwarder,
  setIsForwarder: (value: boolean) => {
    isForwarder = value;
  },
  getIsForwarder: () => isForwarder,
  isCatalogsTestEnv: false,
};

export const useSettings = (allowPlaceholder = true) => {
  const result = apiClient.settings.getSettings.useQuery(
    ['settings'],
    {},
    {
      queryKey: ['settings'],
      staleTime: MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR,
      placeholderData: allowPlaceholder ? ({ body: { data: defaultSettings } } as any) : undefined,
    },
  );
  return { ...result, data: { ...defaultSettings, ...result.data?.body?.data, isForwarder } as Partial<Settings> };
};
